import React, { useState } from 'react'
import { DeleteOutlined, DownOutlined, EditOutlined, PlusOutlined, UpOutlined, } from '@ant-design/icons'
import { Button, Modal, Spin, Tooltip, Typography } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import _ from 'lodash'
import { ContentPartFilter } from '../../../components/content-part/ContentPartFilter'
import { PageHeader } from '../../../components/page-header'
import { EmptyData } from '../../../components/empty/emptyData'
import { useKurultaiRegister } from './useKurultaiRegister'
import { AddKurultaiRegisterModal } from './AddKurultaiRegisterModal'
import { useTranslation } from 'react-i18next'
import { useGetDataOnScroll } from '../../../components/getDataOnScrollProps/useGetDataOnScrollProps'
import { useLazyGetKurultaiRegisterListFilteredQuery } from '../../../redux/api/kurultai/kurultaiRegisterApi'
import { EntKurultaiRegisterFilter } from '../../../models/filters/kurultaiRegisterFilter'
import { FilterKurultaiRegister } from './FilterKurultaiRegister'
import { Assignment, EntKurultaiRegister } from '../../../models/kurultai/kutultaiRegister'
import { useGetReportByKurultaiRegisterMutation } from '../../../redux/api/kurultai/reportKurultaiApi'
import ReportKurultaiModal from './reportKurultaiModal'
import dayjs from 'dayjs'
import { getCookie } from 'typescript-cookie'

enum FormField {
  executionStatusIds,
  regionIds,
  questionCategoryIds,
  responsibleAuthority,
  deadlineDateFrom,
  deadlineDateTo,
  assemblyNumber,
  assemblyDateFrom,
  assemblyDateTo,
  delegateName,
}

export const KurultaiRegister = () => {
  const [modal, contextHolder] = Modal.useModal()
  const [selectedItem, setSelectedItem] = useState<EntKurultaiRegister>(new EntKurultaiRegister())
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showReportModal, setShowReportModal] = useState<boolean>(false)
  const [getKurultaiReport] = useGetReportByKurultaiRegisterMutation()
  const [form] = useForm()
  const { t } = useTranslation()

  const [expandedAssemblies, setExpandedAssemblies] = useState<Record<string, boolean>>({})
  const [expandedRegions, setExpandedRegions] = useState<Record<string, boolean>>({})
  const [expandedAreas, setExpandedAreas] = useState<Record<string, boolean>>({})
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({})

  const toggleAssembly = (assemblyKey: string) => {
	setExpandedAssemblies(prev => ({ ...prev, [assemblyKey]: !prev[assemblyKey] }))
  }
  const toggleRegion = (regionKey: string) => {
	setExpandedRegions(prev => ({ ...prev, [regionKey]: !prev[regionKey] }))
  }
  const toggleArea = (areaKey: string) => {
	setExpandedAreas(prev => ({ ...prev, [areaKey]: !prev[areaKey] }))
  }
  const toggleCategory = (categoryKey: string) => {
	setExpandedCategories(prev => ({ ...prev, [categoryKey]: !prev[categoryKey] }))
  }

  const getReport = async (payload: { languageEnum: string }): Promise<Blob> => {
	return await getKurultaiReport(payload).unwrap()
  }

  const [getKurultaiRegisters] = useLazyGetKurultaiRegisterListFilteredQuery()
  const {
	filter,
	fetching,
	data,
	access,
	totalItems,
	setData,
	setFetching,
	setFilter,
	scrollHandler,
	setCurrentPage,
  } = useGetDataOnScroll<EntKurultaiRegister, EntKurultaiRegisterFilter>({
	initialFilter: new EntKurultaiRegisterFilter(),
	query: getKurultaiRegisters,
  })

  const { onAddKurultaiRegister, onEditKurultaiRegister, onDeleteKurultaiRegister } =
	useKurultaiRegister(modal, () => {
	  searchHandleClick()
	})

  const [height, setHeight] = useState<number>(0)

  const openAddModal = (item: EntKurultaiRegister) => {
	setSelectedItem(item)
	setShowModal(true)
  }

  const openEditModal = (assembly: any, kId: number) => {
	const reg = buildRegisterForEditing(assembly, kId)
	setSelectedItem(reg)
	setShowModal(true)
  }

  const buildRegisterForEditing = (assembly: any, kId: number): EntKurultaiRegister => {
	const newReg = new EntKurultaiRegister()
	newReg.id = kId
	newReg.assemblyNumber = assembly.assemblyNumber
	newReg.assemblyDate = assembly.assemblyDate

	let allAssignments: Assignment[] = []
	assembly.regions?.forEach((reg: any) => {
	  reg.areas?.forEach((areaObj: any) => {
		areaObj.categoryAssignments?.forEach((cat: any) => {
		  cat.assignments?.forEach((ass: any) => {
			allAssignments.push(ass)
		  })
		})
	  })
	})
	allAssignments = allAssignments.filter(a => a.kurultaiRegisterId === kId)

	if (allAssignments.length > 0) {
	  newReg.delegateName = allAssignments[0].delegateName ?? null
	  newReg.territorialUnitId = allAssignments[0].territorialUnitId ?? null
	  newReg.latitude = allAssignments[0].latitude ?? null
	  newReg.longitude = allAssignments[0].longitude ?? null
	}

	let foundRegion: any = null
	let foundArea: any = null

	outerLoop: for (const reg of assembly.regions ?? []) {
	  for (const areaObj of reg.areas ?? []) {
		for (const cat of areaObj.categoryAssignments ?? []) {
		  for (const ass of cat.assignments ?? []) {
			if (ass.kurultaiRegisterId === kId) {
			  foundRegion = reg
			  foundArea = areaObj
			  break outerLoop
			}
		  }
		}
	  }
	}

	if (foundRegion) {
	  newReg.regionId = foundRegion.region.id
	}
	if (foundArea) {
	  newReg.areaId = foundArea.area.id
	  newReg.territorialUnit = foundArea.area
	}

	const finalAssignments = allAssignments.map(a => ({
	  ...a,
	  questionCategoryId: a.questionCategoryId ?? a.questionCategory?.id ?? null,
	  tasksList: (a.tasksList || []).map(task => ({
		...task,
		eventsList: (task.eventsList || []).map(evt => ({
		  ...evt,
		  kurultaiStatusId: evt.kurultaiStatus?.id ?? null,
		})),
	  })),
	}))
	newReg.assignmentsList = finalAssignments

	return newReg
  }

  const setEntityFromFields = (): EntKurultaiRegisterFilter => {
	const filt = _.cloneDeep(filter)
	filt.executionStatusIds = form.getFieldValue(FormField.executionStatusIds) || []
	filt.regionIds = form.getFieldValue(FormField.regionIds) || []
	filt.questionCategoryIds = form.getFieldValue(FormField.questionCategoryIds) || []
	filt.responsibleAuthority = form.getFieldValue(FormField.responsibleAuthority) || ''
	filt.assemblyNumber = form.getFieldValue(FormField.assemblyNumber) || ''
	filt.deadlineDateFrom = form.getFieldValue(FormField.deadlineDateFrom)
	  ? dayjs(form.getFieldValue(FormField.deadlineDateFrom)).format('YYYY-MM-DD')
	  : null

	filt.deadlineDateTo = form.getFieldValue(FormField.deadlineDateTo)
	  ? dayjs(form.getFieldValue(FormField.deadlineDateTo)).format('YYYY-MM-DD')
	  : null

	filt.assemblyDateFrom = form.getFieldValue(FormField.assemblyDateFrom)
	  ? dayjs(form.getFieldValue(FormField.assemblyDateFrom)).format('YYYY-MM-DD')
	  : null

	filt.assemblyDateTo = form.getFieldValue(FormField.assemblyDateTo)
	  ? dayjs(form.getFieldValue(FormField.assemblyDateTo)).format('YYYY-MM-DD')
	  : null
	filt.delegateName = form.getFieldValue(FormField.delegateName) || ''

	filt.page = 1

	return filt
  }

  const clearFields = () => {
	form.resetFields()
  }

  const searchHandleClick = async () => {
	try {
	  await form.validateFields()
	  const updatedFilter = setEntityFromFields()
	  setFilter(updatedFilter)
	  setData([])
	  setCurrentPage(1)
	  setFetching(true)
	} catch (error) {
	  console.error('Ошибка валидации формы:', error)
	}
  }

  const table = (
	<div
	  style={ {
		maxHeight: `${ height }px`,
		overflow: 'auto',
	  } }
	  onScroll={ scrollHandler }
	>
	  <table
		style={ {
		  width: '100%',
		  minWidth: '1500px',
		  tableLayout: 'auto',
		  borderCollapse: 'collapse',
		  whiteSpace: 'normal',
		  wordWrap: 'break-word',
		} }
	  >
		<thead>
		<tr>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.delegate_name') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.question') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.questionDescription') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.tasks') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.indicators') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.deadlineDate') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.eventDetails') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.status') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.responsibleAuthority') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.sourceOfFunding') }</th>
		  <th style={ { textAlign: 'center' } }>{ t('kurultai.actions') }</th>
		</tr>
		</thead>
		<tbody>
		{ fetching && (
		  <tr>
			<td colSpan={ 11 }>
			  <Spin spinning={ fetching }/>
			</td>
		  </tr>
		) }
		{ data && data.length !== 0 ? (
		  data.map((assembly: any) => {
			const assemblyKey = `${ assembly.assemblyNumber }-${ assembly.assemblyDate }`
			return (
			  <React.Fragment key={ assemblyKey }>
				<tr
				  className='assembly-row'
				  onClick={ () => toggleAssembly(assemblyKey) }
				  style={ { cursor: 'pointer', backgroundColor: '#d1c4e9', fontWeight: 'bold' } }
				>
				  <td colSpan={ 11 }>
					{ t('kurultai.assembly_number') }: { assembly.assemblyNumber },{ ' ' }
					{ t('kurultai.assemblyDate') }: { assembly.assemblyDate }
					<Button
					  type='link'
					  icon={ expandedAssemblies[assemblyKey] ? <UpOutlined/> : <DownOutlined/> }
					/>
				  </td>
				</tr>
				{ expandedAssemblies[assemblyKey] &&
				  assembly.regions?.map((regionObj: any) => {
					const regionKey = `${ assemblyKey }-region-${ regionObj.region.id }`
					return (
					  <React.Fragment key={ regionKey }>
						<tr
						  className='region-row'
						  onClick={ () => toggleRegion(regionKey) }
						  style={ {
							cursor: 'pointer',
							backgroundColor: '#f0ad4e',
							fontWeight: 'bold',
						  } }
						>
						  <td colSpan={ 11 }>
							{ getCookie('i18next') === 'ky'
							  ? regionObj.region.nameKy
							  : regionObj.region.nameRu }{ ' ' }
							({ t('kurultai.executionStatus') }:{ ' ' }
							{ regionObj.completionPercentage ?? 0 }%)
							<Button
							  type='link'
							  icon={
								expandedRegions[regionKey] ? <UpOutlined/> : <DownOutlined/>
							  }
							/>
						  </td>
						</tr>
						{ expandedRegions[regionKey] &&
						  regionObj.areas?.map((areaObj: any) => {
							const areaKey = `${ regionKey }-area-${ areaObj.area.id }`
							return (
							  <React.Fragment key={ areaKey }>
								<tr
								  className='area-row'
								  onClick={ () => toggleArea(areaKey) }
								  style={ {
									cursor: 'pointer',
									backgroundColor: '#d9534f',
									color: '#fff',
									fontWeight: 'bold',
								  } }
								>
								  <td colSpan={ 11 }>
									{ getCookie('i18next') === 'ky'
									  ? areaObj.area.nameKy
									  : areaObj.area.nameRu }{ ' ' }
									({ t('kurultai.completionPercentage') }:{ ' ' }
									{ areaObj.completionPercentage ?? 0 }%)
									<Button
									  type='link'
									  icon={
										expandedAreas[areaKey] ? <UpOutlined/> : <DownOutlined/>
									  }
									/>
								  </td>
								</tr>
								{ expandedAreas[areaKey] &&
								  areaObj.categoryAssignments?.map((cat: any, cIndex: number) => {
									const catKey = `${ areaKey }-cat-${ cIndex }`
									return (
									  <React.Fragment key={ catKey }>
										<tr
										  className='category-row'
										  onClick={ () => toggleCategory(catKey) }
										  style={ {
											cursor: 'pointer',
											backgroundColor: '#5bc0de',
											color: '#fff',
											fontWeight: 'bold',
										  } }
										>
										  <td colSpan={ 11 }>
											{ getCookie('i18next') === 'ky'
											  ? cat.categoryNameKy
											  : cat.categoryNameRu }
											<Button
											  type='link'
											  icon={
												expandedCategories[catKey] ? (
												  <UpOutlined/>
												) : (
												  <DownOutlined/>
												)
											  }
											  onClick={ e => {
												e.stopPropagation()
												toggleCategory(catKey)
											  } }
											/>
										  </td>
										</tr>
										{ expandedCategories[catKey] &&
										  cat.assignments?.map((assignment: any) => (
											<React.Fragment key={ assignment.id }>
											  <tr
												className='assignment-row'
												style={ {
												  cursor: 'pointer',
												  backgroundColor: '#e3f2fd',
												  fontWeight: 'bold',
												} }
											  >
												<td style={ { textAlign: 'center' } }>
												  { assignment.delegateName }
												</td>
												<td style={ { textAlign: 'center' } }>
												  { assignment.question }
												</td>
												<td style={ { textAlign: 'center' } }>
												  { assignment.questionDescription }
												</td>
												<td colSpan={ 6 }></td>
												<td style={ { textAlign: 'center' } }>
												  { assignment.sourceOfFunding || '-' }
												</td>
												<td style={ { textAlign: 'center' } }>
												  { access && access.methodPut && (
													<Tooltip title={ t('general.edit') }>
													  <Button
														shape='circle'
														icon={ <EditOutlined/> }
														style={ { marginRight: '5px' } }
														onClick={ e => {
														  e.stopPropagation()
														  openEditModal(
															assembly,
															assignment.kurultaiRegisterId
														  )
														} }
													  />
													</Tooltip>
												  ) }
												  { access && access.methodDelete && (
													<Tooltip title={ t('general.delete') }>
													  <Button
														type='primary'
														shape='circle'
														danger
														icon={ <DeleteOutlined/> }
														onClick={ e => {
														  e.stopPropagation()
														  onDeleteKurultaiRegister({
															id: assignment.kurultaiRegisterId ?? 0,
														  } as EntKurultaiRegister)
														} }
													  />
													</Tooltip>
												  ) }
												</td>
											  </tr>
											  { assignment.tasksList?.map((task: any) =>
												task.eventsList?.map((event: any) => (
												  <tr
													key={ `task-${ task.id }-event-${ event.id }` }
													className='task-row'
												  >
													<td colSpan={ 3 }></td>
													<td style={ { textAlign: 'center' } }>
													  { task.description || '-' }
													</td>
													<td style={ { textAlign: 'center' } }>
													  { event.indicator || '-' }
													</td>
													<td style={ { textAlign: 'center' } }>
													  { event.deadlineDate || '-' }
													</td>
													<td
													  style={ {
														textAlign: 'center',
														maxWidth: '250px',
													  } }
													>
													  { event.eventDetails || '-' }
													</td>
													<td style={ { textAlign: 'center' } }>
													  { event.kurultaiStatus?.nameRu || '-' }
													</td>
													<td style={ { textAlign: 'center' } }>
													  { event.responsibleAuthority || '-' }
													</td>
													<td colSpan={ 2 }></td>
												  </tr>
												))
											  ) }
											</React.Fragment>
										  )) }
									  </React.Fragment>
									)
								  }) }
							  </React.Fragment>
							)
						  }) }
					  </React.Fragment>
					)
				  }) }
			  </React.Fragment>
			)
		  })
		) : (
		  <tr>
			<td colSpan={ 11 }>
			  <EmptyData/>
			</td>
		  </tr>
		) }
		</tbody>
	  </table>
	</div>
  )

  return (
	<>
	  <PageHeader
		title={ t('titles.kurultai') }
		additionalInfo={
		  <Typography.Text style={ { marginBottom: '0.5em', display: 'block' } } type='secondary'>
			{ t('general.records_found') } { totalItems }
		  </Typography.Text>
		}
	  />
	  { access && access.methodPost && (
		<Button
		  type='primary'
		  icon={ <PlusOutlined/> }
		  onClick={ () => {
			openAddModal(new EntKurultaiRegister())
		  } }
		>
		  { t('kurultai.add_kurultai') }
		</Button>
	  ) }
	  { access && access.methodGet && (
	  <Button
		style={ { marginLeft: '5px' } }
		type='primary'
		icon={ <PlusOutlined/> }
		onClick={ () => setShowReportModal(true) }
	  >
		{ t('general.generate_report') }
	  </Button>
		)}
	  <ContentPartFilter
		filter={
		  <FilterKurultaiRegister
			onSearch={ searchHandleClick }
			onReset={ () => {
			  clearFields()
			  searchHandleClick()
			} }
			formField={ FormField }
			form={ form }
		  />
		}
		setHeight={ setHeight }
		table={ table }
	  />

	  <AddKurultaiRegisterModal
		kurultaiRegister={ selectedItem }
		show={ showModal }
		onHide={ () => setShowModal(false) }
		onSubmit={ selectedItem.id ? onEditKurultaiRegister : onAddKurultaiRegister }
	  />

	  <ReportKurultaiModal
		getReport={ getReport }
		show={ showReportModal }
		onHide={ () => setShowReportModal(false) }
	  />

	  { contextHolder }
	</>
  )
}
